import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

function ForeignKeyField({
  name,
  schema,
  value,
  onChange,
  currentValue,
  isLoading,
  error,
  required
}) {
  const { fetchWithAuth } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const foreignKeyConfig = schema.foreign_key;

  const handleSearch = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `/tables/${foreignKeyConfig.table}/search-by-name?q=${encodeURIComponent(searchTerm)}`
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.rows || []);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching foreign key values:', error);
      setSearchResults([]);
    }
  };

  const handleSelect = (selectedItem) => {
    const foreignKeyValue = selectedItem[foreignKeyConfig.column];
    onChange(name, foreignKeyValue, selectedItem);
    setIsEditing(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium dark:text-white">
        {schema.title || name} (Foreign Key)
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        {/* Hidden input for HTML5 validation */}
        <input
          type="text"
          name={name}
          value={value || ''}
          required={required}
          onChange={() => {}} // Controlled by the custom UI
          className="absolute opacity-0 pointer-events-none"
          tabIndex={-1}
        />

        {!isEditing ? (
          <div className="flex items-center space-x-2">
            <div className={`flex-grow p-2 bg-gray-50 dark:bg-gray-700 rounded border dark:border-gray-600 ${
              required && !value ? 'border-red-500' : ''
            }`}>
              {isLoading ? (
                <span className="text-gray-500 dark:text-gray-400">Loading...</span>
              ) : value ? (
                <span className="dark:text-white">{value}</span>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">Not set</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchQuery(value);
                  handleSearch(value);
                }}
                placeholder={`Search ${foreignKeyConfig.table} by name...`}
                className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                autoFocus
              />

              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleSelect(item)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer dark:text-white"
                    >
                      {item.name} ({item[foreignKeyConfig.column]})
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>


      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}

      {schema.description && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {schema.description}
        </p>
      )}
    </div>
  );
}

export default ForeignKeyField;